/**
 * Hickory Golf Shopify Theme - Custom JavaScript
 * Handles all interactive functionality
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const fetchConfig = (type = 'json') => {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/${type}`
      }
    };
  };

  // ============================================
  // Mobile Navigation
  // ============================================

  class MobileNav {
    constructor() {
      this.toggle = document.querySelector('[data-mobile-menu-toggle]');
      this.menu = document.querySelector('[data-mobile-menu]');

      if (this.toggle && this.menu) {
        this.init();
      }
    }

    init() {
      this.toggle.addEventListener('click', () => this.toggleMenu());

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.menu.hidden) {
          this.closeMenu();
        }
      });
    }

    toggleMenu() {
      const isOpen = !this.menu.hidden;
      if (isOpen) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    openMenu() {
      this.menu.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Close menu');
      document.body.style.overflow = 'hidden';
    }

    closeMenu() {
      this.menu.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', 'Open menu');
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Search Overlay
  // ============================================

  class SearchOverlay {
    constructor() {
      this.toggleBtn = document.querySelector('[data-search-toggle]');
      this.overlay = document.querySelector('[data-search-overlay]');
      this.closeBtn = document.querySelector('[data-search-close]');
      this.input = this.overlay?.querySelector('input');

      if (this.toggleBtn && this.overlay) {
        this.init();
      }
    }

    init() {
      this.toggleBtn.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.overlay.hidden) {
          this.close();
        }
      });

      // Close on overlay click
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });
    }

    open() {
      this.overlay.hidden = false;
      this.input?.focus();
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.overlay.hidden = true;
      document.body.style.overflow = '';
      this.toggleBtn.focus();
    }
  }

  // ============================================
  // Product Gallery
  // ============================================

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.main = container.querySelector('.product-gallery__main');
      this.slides = container.querySelectorAll('.product-gallery__slide');
      this.thumbs = container.querySelectorAll('.product-gallery__thumb');
      this.counter = container.querySelector('[data-gallery-counter]');
      this.currentIndex = 0;

      if (this.slides.length > 1) {
        this.init();
      }
    }

    init() {
      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Arrow buttons (tap / click)
      const prevBtn = this.container.querySelector('[data-gallery-prev]');
      const nextBtn = this.container.querySelector('[data-gallery-next]');
      prevBtn?.addEventListener('click', () => this.prev());
      nextBtn?.addEventListener('click', () => this.next());

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      // Touch swipe + mouse drag on the main image
      if (this.main) this.bindSwipe(this.main);

      // Tap (mobile) / click (desktop) the photo to advance to the next one
      if (this.main) {
        this.main.addEventListener('click', (e) => {
          if (e.target.closest('.product-gallery__nav')) return; // arrows handle themselves
          if (this._suppressClick) { this._suppressClick = false; return; } // was a drag/swipe
          this.next();
        });
        this.main.classList.add('is-tappable');
      }
    }

    bindSwipe(el) {
      const THRESHOLD = 40; // px of horizontal travel to count as a swipe
      let startX = 0;
      let startY = 0;
      let tracking = false;

      const start = (x, y) => {
        startX = x;
        startY = y;
        tracking = true;
      };
      const end = (x, y) => {
        if (!tracking) return;
        tracking = false;
        const dx = x - startX;
        const dy = y - startY;
        // Only act on a mostly-horizontal swipe so vertical scrolling is unaffected
        if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) this.next();
          else this.prev();
          this._suppressClick = true; // don't also tap-advance on the trailing click
        } else if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          this._suppressClick = true; // a drag/scroll, not a tap
        }
      };

      // Touch
      el.addEventListener('touchstart', (e) => {
        const t = e.changedTouches[0];
        start(t.clientX, t.clientY);
      }, { passive: true });
      el.addEventListener('touchend', (e) => {
        const t = e.changedTouches[0];
        end(t.clientX, t.clientY);
      }, { passive: true });

      // Mouse drag (desktop)
      el.addEventListener('mousedown', (e) => {
        start(e.clientX, e.clientY);
      });
      window.addEventListener('mouseup', (e) => {
        if (tracking) end(e.clientX, e.clientY);
      });
    }

    goToSlide(index) {
      this.slides[this.currentIndex].classList.remove('product-gallery__slide--active');
      this.thumbs[this.currentIndex]?.classList.remove('product-gallery__thumb--active');

      this.currentIndex = index;

      this.slides[this.currentIndex].classList.add('product-gallery__slide--active');
      this.thumbs[this.currentIndex]?.classList.add('product-gallery__thumb--active');
      this.thumbs[this.currentIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      if (this.counter) this.counter.textContent = (this.currentIndex + 1) + ' / ' + this.slides.length;
    }

    prev() {
      const newIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
      this.goToSlide(newIndex);
    }

    next() {
      const newIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
      this.goToSlide(newIndex);
    }
  }

  // ============================================
  // Product Form
  // ============================================

  class ProductForm {
    constructor(form) {
      this.form = form;
      this.submitBtn = form.querySelector('[data-add-to-cart]');
      this.submitText = form.querySelector('[data-add-to-cart-text]');
      this.variantIdInput = form.querySelector('[data-variant-id]');
      this.optionInputs = form.querySelectorAll('[data-option-input]');

      const variantData = form.closest('[data-product-section]')?.querySelector('[data-product-variants]');
      this.variants = variantData ? JSON.parse(variantData.textContent) : [];

      this.init();
    }

    init() {
      // Variant selection
      this.optionInputs.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });

      // Form submission
      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      // Buy Now button
      const buyNowBtn = this.form.querySelector('[data-buy-now]');
      if (buyNowBtn) {
        buyNowBtn.addEventListener('click', () => this.onBuyNow());
      }

      // Quantity buttons
      this.initQuantityButtons();
    }

    initQuantityButtons() {
      const minusBtn = this.form.querySelector('[data-quantity-minus]');
      const plusBtn = this.form.querySelector('[data-quantity-plus]');
      const input = this.form.querySelector('[data-quantity-input]');

      if (minusBtn && plusBtn && input) {
        minusBtn.addEventListener('click', () => {
          const newValue = Math.max(1, parseInt(input.value) - 1);
          input.value = newValue;
        });

        plusBtn.addEventListener('click', () => {
          const newValue = parseInt(input.value) + 1;
          input.value = newValue;
        });
      }
    }

    onVariantChange() {
      const selectedOptions = Array.from(this.optionInputs)
        .filter(input => input.checked)
        .map(input => input.value);

      // Update option value displays
      this.optionInputs.forEach(input => {
        if (input.checked) {
          const label = this.form.querySelector(`[data-option-value="${input.name}"]`);
          if (label) label.textContent = input.value;

          // Update swatch selected state
          const swatch = input.closest('.product-form__swatch');
          if (swatch) {
            swatch.parentElement.querySelectorAll('.product-form__swatch').forEach(s => {
              s.classList.remove('product-form__swatch--selected');
            });
            swatch.classList.add('product-form__swatch--selected');
          }
        }
      });

      // Find variant matching selected options and update the form
      const matchingVariant = this.variants.find(variant =>
        selectedOptions.every((val, i) => variant.options[i] === val)
      );

      if (matchingVariant) {
        this.variantIdInput.value = matchingVariant.id;
      }
    }

    async onSubmit(e) {
      e.preventDefault();

      if (this.submitBtn.disabled) return;

      this.submitBtn.disabled = true;
      if (this.submitText) this.submitText.textContent = 'Adding...';

      const formData = new FormData(this.form);
      const data = {
        id: formData.get('id'),
        quantity: formData.get('quantity')
      };

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify(data)
        });

        if (!response.ok) throw new Error('Add to cart failed');

        const cart = await response.json();

        // Update cart count
        const cartCount = document.querySelector('[data-cart-count]');
        if (cartCount) {
          cartCount.textContent = cart.item_count || '';
        }

        // Open the slide-out cart drawer instead of redirecting to /cart
        if (this.submitText) this.submitText.textContent = 'Add to Cart';
        if (window.owhgOpenCart) window.owhgOpenCart();
        else window.location.href = '/cart';

      } catch (error) {
        console.error('Error:', error);
        if (this.submitText) this.submitText.textContent = 'Error - Try Again';
      } finally {
        this.submitBtn.disabled = false;
      }
    }

    async onBuyNow() {
      const buyNowBtn = this.form.querySelector('[data-buy-now]');
      if (buyNowBtn.disabled) return;

      const orig = buyNowBtn.innerHTML;
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = 'Adding…';

      const formData = new FormData(this.form);
      const id = parseInt(formData.get('id'), 10);
      const quantity = parseInt(formData.get('quantity'), 10) || 1;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: quantity }] })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        window.location.href = '/checkout';
      } catch (error) {
        buyNowBtn.innerHTML = orig;
        buyNowBtn.disabled = false;
      }
    }
  }

  // ============================================
  // Product Tabs
  // ============================================

  class ProductTabs {
    constructor(container) {
      this.container = container;
      this.buttons = container.querySelectorAll('[data-tab]');
      this.panels = container.querySelectorAll('[data-tab-panel]');

      this.init();
    }

    init() {
      this.buttons.forEach(button => {
        button.addEventListener('click', () => {
          const tabId = button.dataset.tab;
          this.activateTab(tabId);
        });
      });
    }

    activateTab(tabId) {
      // Update buttons
      this.buttons.forEach(btn => {
        btn.classList.toggle('product-tabs__btn--active', btn.dataset.tab === tabId);
      });

      // Update panels
      this.panels.forEach(panel => {
        panel.classList.toggle('product-tabs__panel--active', panel.dataset.tabPanel === tabId);
      });
    }
  }

  // ============================================
  // Hero Carousel
  // ============================================

  class HeroCarousel {
    constructor(container) {
      this.container = container;
      this.slides = container.querySelectorAll('.hero-banner__slide');
      this.dots = container.querySelectorAll('.hero-banner__dot');
      this.prevBtn = container.querySelector('[data-carousel-prev]');
      this.nextBtn = container.querySelector('[data-carousel-next]');
      this.currentIndex = 0;
      this.autoplayInterval = null;
      this.autoplaySpeed = parseInt(container.dataset.autoplaySpeed || '5') * 1000;

      if (this.slides.length > 1) {
        this.init();
      }
    }

    init() {
      this.prevBtn?.addEventListener('click', () => { this.prev(); this.restartAutoplay(); });
      this.nextBtn?.addEventListener('click', () => { this.next(); this.restartAutoplay(); });

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { this.goToSlide(i); this.restartAutoplay(); });
      });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.prev(); this.restartAutoplay(); }
        if (e.key === 'ArrowRight') { this.next(); this.restartAutoplay(); }
      });

      // Pause on hover
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());

      // Start autoplay
      if (this.autoplaySpeed > 0) {
        this.startAutoplay();
      }
    }

    goToSlide(index) {
      this.slides[this.currentIndex].classList.remove('hero-banner__slide--active');
      this.dots[this.currentIndex]?.classList.remove('hero-banner__dot--active');
      this.dots[this.currentIndex]?.setAttribute('aria-selected', 'false');

      this.currentIndex = index;

      this.slides[this.currentIndex].classList.add('hero-banner__slide--active');
      this.dots[this.currentIndex]?.classList.add('hero-banner__dot--active');
      this.dots[this.currentIndex]?.setAttribute('aria-selected', 'true');
    }

    prev() {
      const newIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
      this.goToSlide(newIndex);
    }

    next() {
      const newIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
      this.goToSlide(newIndex);
    }

    startAutoplay() {
      if (this.autoplaySpeed > 0) {
        this.autoplayInterval = setInterval(() => this.next(), this.autoplaySpeed);
      }
    }

    stopAutoplay() {
      clearInterval(this.autoplayInterval);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  // ============================================
  // Testimonials Slider
  // ============================================

  class TestimonialsSlider {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('.testimonials__track');
      this.testimonials = container.querySelectorAll('.testimonial');
      this.prevBtn = container.querySelector('[data-testimonial-prev]');
      this.nextBtn = container.querySelector('[data-testimonial-next]');
      this.dots = container.querySelectorAll('[data-testimonial-dot]');
      this.currentIndex = 0;
      this.autoplayInterval = null;

      if (this.testimonials.length > 1) {
        this.init();
      }
    }

    init() {
      // Hide all but first
      this.testimonials.forEach((t, i) => {
        t.style.display = i === 0 ? 'block' : 'none';
      });

      // Navigation
      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goToSlide(i));
      });

      // Autoplay
      this.startAutoplay();

      // Pause on hover
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
    }

    goToSlide(index) {
      this.testimonials[this.currentIndex].style.display = 'none';
      this.dots[this.currentIndex]?.classList.remove('testimonials__dot--active');

      this.currentIndex = index;

      this.testimonials[this.currentIndex].style.display = 'block';
      this.dots[this.currentIndex]?.classList.add('testimonials__dot--active');
    }

    prev() {
      const newIndex = this.currentIndex === 0 ? this.testimonials.length - 1 : this.currentIndex - 1;
      this.goToSlide(newIndex);
    }

    next() {
      const newIndex = this.currentIndex === this.testimonials.length - 1 ? 0 : this.currentIndex + 1;
      this.goToSlide(newIndex);
    }

    startAutoplay() {
      // Autoplay disabled — let users browse at their own pace
    }

    stopAutoplay() {
      clearInterval(this.autoplayInterval);
    }
  }

  // ============================================
  // Cart Functionality
  // ============================================

  class Cart {
    constructor() {
      this.form = document.querySelector('[data-cart-form]');
      this.items = document.querySelectorAll('[data-cart-item]');

      if (this.form) {
        this.init();
      }
    }

    init() {
      // Quantity buttons
      this.items.forEach(item => {
        const minusBtn = item.querySelector('[data-quantity-minus]');
        const plusBtn = item.querySelector('[data-quantity-plus]');
        const input = item.querySelector('[data-quantity-input]');
        const removeBtn = item.querySelector('[data-remove-item]');

        if (minusBtn && plusBtn && input) {
          minusBtn.addEventListener('click', () => {
            const newValue = Math.max(0, parseInt(input.value) - 1);
            input.value = newValue;
            this.updateItem(input.dataset.line, newValue);
          });

          plusBtn.addEventListener('click', () => {
            const newValue = parseInt(input.value) + 1;
            input.value = newValue;
            this.updateItem(input.dataset.line, newValue);
          });

          input.addEventListener('change', debounce(() => {
            this.updateItem(input.dataset.line, parseInt(input.value));
          }, 300));
        }

        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            this.updateItem(removeBtn.dataset.line, 0);
          });
        }
      });
    }

    async updateItem(line, quantity) {
      try {
        const response = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            line: parseInt(line),
            quantity: quantity
          })
        });

        if (!response.ok) throw new Error('Cart update failed');

        // Reload page to show updated cart
        // In production, you'd update the DOM dynamically
        window.location.reload();

      } catch (error) {
        console.error('Error:', error);
      }
    }
  }

  // ============================================
  // Collection Filters
  // ============================================

  class CollectionFilters {
    constructor() {
      this.filterToggle = document.querySelector('[data-filter-toggle]');
      this.sidebar = document.querySelector('[data-filter-sidebar]');
      this.closeBtn = document.querySelector('[data-filter-close]');
      this.backdrop = document.querySelector('[data-filter-backdrop]');
      this.form = document.querySelector('[data-filter-form]');
      this.sortSelect = document.querySelector('[data-sort-select]');

      this.init();
    }

    init() {
      // Mobile filter toggle
      this.filterToggle?.addEventListener('click', () => this.openSidebar());

      this.closeBtn?.addEventListener('click', () => this.closeSidebar());

      this.backdrop?.addEventListener('click', () => this.closeSidebar());

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sidebar?.classList.contains('is-open')) {
          this.closeSidebar();
        }
      });

      // Filter form submit
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyFilters();
      });

      // Sort select
      this.sortSelect?.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.sortSelect.value);
        window.location.href = url.toString();
      });

      // Filter group toggles
      document.querySelectorAll('[data-filter-toggle-group]').forEach(toggle => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !isExpanded);
          toggle.nextElementSibling.hidden = isExpanded;
        });
      });
    }

    openSidebar() {
      this.sidebar?.classList.add('is-open');
      this.backdrop?.classList.add('is-visible');
      if (this.backdrop) this.backdrop.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    closeSidebar() {
      this.sidebar?.classList.remove('is-open');
      this.backdrop?.classList.remove('is-visible');
      document.body.style.overflow = '';
      setTimeout(() => { if (this.backdrop) this.backdrop.hidden = true; }, 300);
    }

    applyFilters() {
      const formData = new FormData(this.form);
      const url = new URL(window.location.href);

      // Clear existing filter params
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.')) {
          url.searchParams.delete(key);
        }
      }

      // Add new filter params
      for (const [key, value] of formData.entries()) {
        if (value) {
          url.searchParams.append(key, value);
        }
      }

      window.location.href = url.toString();
    }
  }

  // ============================================
  // Quick Add to Cart
  // ============================================

  class QuickAdd {
    constructor() {
      this.init();
    }

    init() {
      document.querySelectorAll('.product-card__quick-add').forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();

          const button = form.querySelector('button');
          const originalText = button.textContent;

          button.disabled = true;
          button.textContent = 'Adding...';

          const formData = new FormData(form);
          const data = {
            id: formData.get('id'),
            quantity: 1
          };

          try {
            const response = await fetch(window.routes.cart_add_url, {
              ...fetchConfig(),
              body: JSON.stringify(data)
            });

            if (!response.ok) throw new Error('Add to cart failed');

            const cart = await response.json();

            // Update cart count
            const cartCount = document.querySelector('[data-cart-count]');
            if (cartCount) {
              cartCount.textContent = cart.item_count || '';
            }

            button.textContent = originalText;
            if (window.owhgOpenCart) window.owhgOpenCart();

          } catch (error) {
            console.error('Error:', error);
            button.textContent = 'Error';
          } finally {
            button.disabled = false;
          }
        });
      });
    }
  }

  // ============================================
  // View Toggle (Grid/List)
  // ============================================

  class ViewToggle {
    constructor() {
      this.buttons = document.querySelectorAll('[data-view]');
      this.grid = document.querySelector('[data-product-grid]');

      if (this.buttons.length && this.grid) {
        this.init();
      }
    }

    init() {
      // Check saved preference
      const savedView = localStorage.getItem('collection-view');
      if (savedView) {
        this.setView(savedView);
      }

      this.buttons.forEach(button => {
        button.addEventListener('click', () => {
          const view = button.dataset.view;
          this.setView(view);
          localStorage.setItem('collection-view', view);
        });
      });
    }

    setView(view) {
      this.buttons.forEach(btn => {
        btn.classList.toggle('view-toggle__btn--active', btn.dataset.view === view);
      });

      this.grid.classList.toggle('collection-products__grid--list', view === 'list');
    }
  }

  // ============================================
  // Smooth Scroll for Anchor Links
  // ============================================

  class SmoothScroll {
    constructor() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const target = document.querySelector(anchor.getAttribute('href'));
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });
    }
  }

  // ============================================
  // Sticky Mobile Add to Cart Bar
  // ============================================

  class StickyBar {
    constructor() {
      this.bar = document.querySelector('[data-sticky-bar]');
      this.addBtn = document.querySelector('[data-sticky-add-to-cart]');
      this.buyBtn = document.querySelector('[data-sticky-buy-now]');
      this.productForm = document.querySelector('[data-product-form]');

      if (!this.bar || !this.addBtn || !this.productForm) return;

      this.formSubmitBtn = this.productForm.querySelector('[data-add-to-cart]');
      this.init();
    }

    init() {
      // Show/hide based on scroll position relative to main Add to Cart button
      const observer = new IntersectionObserver(
        ([entry]) => {
          this.bar.classList.toggle('product-sticky-bar--visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      if (this.formSubmitBtn) {
        observer.observe(this.formSubmitBtn);
      }

      // Add to cart - trigger the real form submit (lands on the cart)
      this.addBtn.addEventListener('click', () => {
        if (this.formSubmitBtn) {
          this.formSubmitBtn.click();
        }
      });

      // Buy now - add the item, then go straight to checkout
      if (this.buyBtn) {
        this.buyBtn.addEventListener('click', () => this.buyNow());
      }
    }

    buyNow() {
      const idInput = this.productForm.querySelector('[data-variant-id]') || this.productForm.querySelector('[name="id"]');
      const id = idInput ? parseInt(idInput.value, 10) : null;
      const qtyInput = this.productForm.querySelector('[data-quantity-input]');
      const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      if (!id) return;
      const orig = this.buyBtn.innerHTML;
      this.buyBtn.disabled = true;
      this.buyBtn.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
      }).then((r) => {
        if (!r.ok) throw new Error('add failed');
        window.location.href = '/checkout';
      }).catch(() => {
        this.buyBtn.disabled = false;
        this.buyBtn.innerHTML = orig;
      });
    }
  }

  // ============================================
  // Club Builder
  // ============================================

  class ClubBuilder {
    constructor(root) {
      this.root = root;
      this.steps = root.querySelectorAll('[data-cb-step]');
      this.pills = root.querySelectorAll('[data-cb-step-pill]');
      this.summary = root.querySelector('[data-cb-summary]');

      // Selection state.
      // clubs holds one {shaft, grip} per head in the set (1 for a normal head,
      // 2 or 3 for a multi-head set). headCount drives the per-club flow.
      this.state = {
        head: null,
        headCount: 1,
        clubs: [{ shaft: null, grip: null }],
        assembly: null,
        boreOut: false,
        rebuildKit: false
      };
      this.currentClub = 0;
      this.sequence = [];   // ordered list of {step, club} stops after the head is picked
      this.flowPos = -1;    // index into sequence (-1 = on the head step)

      this.bindHeadFilter();
      this.bindCards();
      this.bindBack();
      this.bindClearAll();
      this.bindFaq();
      this.bindBoreOutToggle();
      this.bindRebuildKitToggle();
      this.bindAddToCart();
      this.bindBuildAnother();
      this.updateReadyDate();
      this.applyPreselectFromUrl();
    }

    // Launch from a head's product page: /pages/club-builder?head=<handle>
    // preselects that head and jumps the customer straight to the shaft step.
    applyPreselectFromUrl() {
      let handle = null;
      try {
        handle = new URLSearchParams(window.location.search).get('head');
      } catch (e) {
        return;
      }
      if (!handle) return;
      const safe = (window.CSS && CSS.escape) ? CSS.escape(handle) : handle.replace(/"/g, '\\"');
      const card = this.root.querySelector(
        '[data-cb-head-grid] [data-cb-pick="head"][data-cb-handle="' + safe + '"]'
      );
      // No match (typo / not a builder head) or sold out -> leave them on step 1.
      if (!card || card.hasAttribute('disabled')) return;
      // Reuse the normal selection path: sets head state, filters shafts, shows step 2.
      this.pick('head', card);
    }

    formatDate(date) {
      const opts = { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' };
      return date.toLocaleDateString('en-US', opts);
    }

    updateReadyDate() {
      const target = new Date();
      target.setDate(target.getDate() + 14);
      const formatted = this.formatDate(target);
      const el = this.root.querySelector('[data-cb-ready-date]');
      if (el) el.textContent = formatted;
      this.readyByStr = formatted;
    }

    bindBuildAnother() {
      const btn = this.root.querySelector('[data-cb-build-another]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const added = this.root.querySelector('[data-cb-added]');
        if (added) added.hidden = true;
        const addBtn = this.root.querySelector('[data-cb-add]');
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add Build to Cart'; }
        this.clearAll();
      });
    }

    bindBoreOutToggle() {
      const toggle = this.root.querySelector('[data-cb-bore-toggle]');
      if (!toggle) return;
      toggle.addEventListener('change', () => {
        this.state.boreOut = toggle.checked;
        this.renderSummary();
      });
    }

    bindRebuildKitToggle() {
      const toggle = this.root.querySelector('[data-cb-kit-toggle]');
      if (!toggle) return;
      toggle.addEventListener('change', () => {
        this.state.rebuildKit = toggle.checked;
        this.renderSummary();
      });
    }

    bindClearAll() {
      const btn = this.root.querySelector('[data-cb-clear-all]');
      if (!btn) return;
      btn.addEventListener('click', () => this.clearAll());
    }

    clearAll() {
      // Reset selection state
      this.state = { head: null, headCount: 1, clubs: [{ shaft: null, grip: null }], assembly: null, boreOut: false, rebuildKit: false };
      this.currentClub = 0;
      this.sequence = [];
      this.flowPos = -1;
      // Clear the dynamic per-club summary rows + progress labels
      const clubsBox = this.root.querySelector('[data-cb-sum-clubs]');
      if (clubsBox) clubsBox.innerHTML = '';
      this.root.querySelectorAll('[data-cb-club-progress]').forEach((el) => { el.hidden = true; el.textContent = ''; });
      // Uncheck the bore-out toggle
      const boreToggle = this.root.querySelector('[data-cb-bore-toggle]');
      if (boreToggle) boreToggle.checked = false;
      // Uncheck the rebuild-kit toggle
      const kitToggle = this.root.querySelector('[data-cb-kit-toggle]');
      if (kitToggle) kitToggle.checked = false;

      // Clear visual selection on every card
      this.root.querySelectorAll('[data-cb-pick].is-selected').forEach((card) => {
        card.classList.remove('is-selected');
      });

      // Reset head filter back to "All"
      const filterBtns = this.root.querySelectorAll('[data-cb-filter]');
      filterBtns.forEach((b) => b.classList.remove('is-active'));
      const allBtn = this.root.querySelector('[data-cb-filter="all"]');
      if (allBtn) allBtn.classList.add('is-active');
      this.root.querySelectorAll('[data-cb-head-grid] [data-cb-pick="head"]').forEach((card) => {
        card.hidden = false;
      });

      // Unhide all shafts (they were filtered by head compat)
      this.root.querySelectorAll('[data-cb-shaft-grid] [data-cb-pick="shaft"]').forEach((s) => {
        s.hidden = false;
      });

      // Clear recap titles
      const recapTitle = this.root.querySelector('[data-cb-recap-head-title]');
      if (recapTitle) recapTitle.textContent = '';

      // Hide summary panel
      if (this.summary) this.summary.hidden = true;

      // Hide post-add confirmation if visible
      const added = this.root.querySelector('[data-cb-added]');
      if (added) added.hidden = true;

      // Clear any pending error from the previous attempt
      const errEl = this.root.querySelector('[data-cb-error]');
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }

      // Restore the default (wood) assembly fee
      this.applyAssemblyFee('wood');
      this.applyCategoryCopy('wood');

      // Return to step 1
      this.showStep(1);
    }

    bindHeadFilter() {
      const buttons = this.root.querySelectorAll('[data-cb-filter]');
      const cards = this.root.querySelectorAll('[data-cb-head-grid] [data-cb-pick="head"]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          buttons.forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          const filter = btn.dataset.cbFilter;
          cards.forEach((card) => {
            if (filter === 'all' || card.dataset.cbCategory === filter) {
              card.hidden = false;
            } else {
              card.hidden = true;
            }
          });
        });
      });
    }

    bindCards() {
      this.root.querySelectorAll('[data-cb-pick]').forEach((card) => {
        card.addEventListener('click', () => {
          if (card.hasAttribute('disabled')) return;
          const role = card.dataset.cbPick;
          this.pick(role, card);
        });
      });
    }

    bindBack() {
      // Step-to-step back walks one stop back through the build sequence, so it
      // works for both a single head and a 2-/3-club set.
      this.root.querySelectorAll('[data-cb-flow-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.goToStop(this.flowPos - 1));
      });
      // "change" next to the head recap always jumps back to the head picker.
      this.root.querySelectorAll('[data-cb-change-head]').forEach((btn) => {
        btn.addEventListener('click', () => this.goToStop(-1));
      });
    }

    bindFaq() {
      this.root.querySelectorAll('.cb-faq__item').forEach((item) => {
        const btn = item.querySelector('.cb-faq__q');
        const ans = item.querySelector('.cb-faq__a');
        btn.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          this.root.querySelectorAll('.cb-faq__item').forEach((other) => {
            other.classList.remove('is-open');
            other.querySelector('.cb-faq__q').setAttribute('aria-expanded', 'false');
            other.querySelector('.cb-faq__a').style.maxHeight = null;
          });
          if (!isOpen) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            ans.style.maxHeight = ans.scrollHeight + 'px';
          }
        });
      });
    }

    // Show the copy variant that matches the selected head's construction.
    // Woods are splice-necked: whipped, back-screwed, neck putty. Irons are
    // socketed: bored, pinned, glued. Saying the wrong one is a factual error,
    // so each claim is scoped to the category it's true for.
    applyCategoryCopy(category) {
      const isIron = (category === 'iron' || category === 'putter');
      this.root.querySelectorAll('[data-cb-copy]').forEach((el) => {
        const want = el.dataset.cbCopy;
        el.hidden = isIron ? (want !== 'iron') : (want !== 'wood');
      });
    }

    // Swap the assembled-build fee to match the selected head's category.
    // Irons and putters are pinned, not whipped, so they carry a lower fee.
    // Falls back to the wood fee if the iron fee product is missing or
    // unpublished, so a build never silently ships with no fee line item.
    applyAssemblyFee(category) {
      const card = this.root.querySelector('[data-cb-pick="assembly"][data-cb-assembly="assembled"]');
      if (!card) return;
      const d = card.dataset;
      const useIron = (category === 'iron' || category === 'putter');
      let cents = d.cbFeeWoodCents;
      let variant = d.cbFeeWoodVariant;
      if (useIron && d.cbFeeIronCents && d.cbFeeIronVariant) {
        cents = d.cbFeeIronCents;
        variant = d.cbFeeIronVariant;
      }
      if (cents) d.cbPriceCents = cents;
      if (variant) d.cbVariantId = variant;
      const priceEl = card.querySelector('[data-cb-fee-price]');
      if (priceEl && cents) {
        priceEl.textContent = '+' + this.money(parseInt(cents, 10)) + ' build fee';
      }
      // If the customer already chose "assembled", refresh the stored pick so
      // the summary and cart use the new fee instead of the stale one.
      if (this.state.assembly && this.state.assembly.assembly === 'assembled') {
        this.state.assembly = this.selectionFromCard(card);
        this.renderSummary();
      }
    }

    selectionFromCard(card) {
      return {
        variantId: card.dataset.cbVariantId || null,
        handle: card.dataset.cbHandle || null,
        title: card.dataset.cbTitle || '',
        priceCents: parseInt(card.dataset.cbPriceCents || '0', 10),
        priceFormatted: card.dataset.cbPriceFormatted || '',
        category: card.dataset.cbCategory || null,
        compat: card.dataset.cbCompat || null,
        shaftType: card.dataset.cbShaftType || null,
        gripKind: card.dataset.cbGripKind || null,
        assembly: card.dataset.cbAssembly || null
      };
    }

    pick(role, card) {
      const sel = this.selectionFromCard(card);
      const markOnly = () => {
        this.root.querySelectorAll(`[data-cb-pick="${role}"]`).forEach((s) => s.classList.remove('is-selected'));
        card.classList.add('is-selected');
      };

      if (role === 'head') {
        markOnly();
        this.state.head = sel;
        let n = parseInt(card.dataset.cbHeadCount || '1', 10);
        if (!(n >= 2 && n <= 3)) n = 1;
        this.state.headCount = n;
        this.state.clubs = Array.from({ length: n }, () => ({ shaft: null, grip: null }));
        this.currentClub = 0;
        this.filterShaftsByHead();
        this.applyAssemblyFee(sel.category);
        this.applyCategoryCopy(sel.category);
        this.buildSequence();
        this.goToStop(0); // first club's shaft
      } else if (role === 'shaft') {
        markOnly();
        this.state.clubs[this.currentClub].shaft = sel;
        this.goToStop(this.flowPos + 1); // grip for the same club
      } else if (role === 'grip') {
        markOnly();
        this.state.clubs[this.currentClub].grip = sel;
        this.goToStop(this.flowPos + 1); // next club's shaft, or the assembly step
      } else if (role === 'assembly') {
        markOnly();
        this.state.assembly = sel;
        this.renderSummary();
      }
    }

    // Ordered stops the customer walks after picking the head: a shaft + grip
    // pick per club, then the single assembly choice for the whole set.
    buildSequence() {
      const seq = [];
      for (let c = 0; c < this.state.headCount; c++) {
        seq.push({ step: 2, club: c });
        seq.push({ step: 3, club: c });
      }
      seq.push({ step: 4, club: null });
      this.sequence = seq;
    }

    goToStop(pos) {
      if (pos < 0) {
        this.flowPos = -1;
        this.showStep(1);
        return;
      }
      if (pos >= this.sequence.length) pos = this.sequence.length - 1;
      this.flowPos = pos;
      const stop = this.sequence[pos];
      if (stop.club !== null) this.currentClub = stop.club;
      this.showStep(stop.step);
      this.updateClubProgress(stop);
      // Reflect this club's prior choice (fresh = cleared) on the grid.
      if (stop.step === 2) this.refreshSelectionUI('shaft', this.state.clubs[stop.club].shaft);
      if (stop.step === 3) this.refreshSelectionUI('grip', this.state.clubs[stop.club].grip);
    }

    refreshSelectionUI(role, sel) {
      const cards = this.root.querySelectorAll(`[data-cb-pick="${role}"]`);
      cards.forEach((c) => c.classList.remove('is-selected'));
      if (sel && sel.variantId) {
        cards.forEach((c) => {
          if (c.dataset.cbVariantId === String(sel.variantId)) c.classList.add('is-selected');
        });
      }
    }

    updateClubProgress(stop) {
      const show = this.state.headCount > 1 && stop && stop.club !== null;
      this.root.querySelectorAll('[data-cb-club-progress]').forEach((el) => {
        el.hidden = !show;
        if (show) el.textContent = 'Building club ' + (stop.club + 1) + ' of ' + this.state.headCount;
      });
    }

    filterShaftsByHead() {
      const head = this.state.head;
      if (!head) return;
      const compat = (head.compat || '').split(',').map((s) => s.trim()).filter(Boolean);
      const shafts = this.root.querySelectorAll('[data-cb-shaft-grid] [data-cb-pick="shaft"]');
      shafts.forEach((s) => {
        const shaftType = s.dataset.cbShaftType;
        if (compat.length === 0 || compat.indexOf(shaftType) !== -1) {
          s.hidden = false;
        } else {
          s.hidden = true;
        }
      });
      // Surface recap
      const titleEl = this.root.querySelector('[data-cb-recap-head-title]');
      if (titleEl) titleEl.textContent = head.title;
    }

    showStep(n) {
      this.steps.forEach((step) => {
        step.classList.toggle('is-active', parseInt(step.dataset.cbStep, 10) === n);
      });
      this.pills.forEach((pill) => {
        const pn = parseInt(pill.dataset.cbStepPill, 10);
        pill.classList.remove('is-active', 'is-done');
        if (pn < n) pill.classList.add('is-done');
        if (pn === n) pill.classList.add('is-active');
      });
      // Scroll to top of section
      const target = this.root.querySelector(`[data-cb-step="${n}"]`);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    money(cents) {
      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    }

    renderSummary() {
      const s = this.state;
      const n = s.headCount;
      const set = (sel, val) => {
        const el = this.root.querySelector(sel);
        if (el) el.textContent = val;
      };
      const esc = (str) => String(str).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

      // Head (the set itself is one line; note the head count)
      set('[data-cb-sum-head-title]', s.head ? (s.head.title + (n > 1 ? ' (' + n + ' heads)' : '')) : '—');
      set('[data-cb-sum-head-price]', s.head ? s.head.priceFormatted : '');

      // One shaft + grip row per club, built dynamically.
      let shaftsCents = 0;
      let gripsCents = 0;
      let clubsHtml = '';
      s.clubs.forEach((club, i) => {
        if (n > 1) clubsHtml += '<div class="cb-summary__club-head">Club ' + (i + 1) + '</div>';
        const sTitle = club.shaft ? esc(club.shaft.title) : '—';
        const sPrice = club.shaft ? esc(club.shaft.priceFormatted) : '';
        const gTitle = club.grip ? esc(club.grip.title) : '—';
        const gPrice = club.grip ? esc(club.grip.priceFormatted) : '';
        clubsHtml += '<div class="cb-summary__row"><dt>Shaft</dt><dd><span>' + sTitle + '</span> <span>' + sPrice + '</span></dd></div>';
        clubsHtml += '<div class="cb-summary__row"><dt>Grip</dt><dd><span>' + gTitle + '</span> <span>' + gPrice + '</span></dd></div>';
        if (club.shaft) shaftsCents += club.shaft.priceCents;
        if (club.grip) gripsCents += club.grip.priceCents;
      });
      const clubsBox = this.root.querySelector('[data-cb-sum-clubs]');
      if (clubsBox) clubsBox.innerHTML = clubsHtml;

      // Assembly is one choice for the whole set; the build fee is per club.
      let assemblyTitle = '—';
      let assemblyPrice = '';
      let assemblyCents = 0;
      if (s.assembly) {
        if (s.assembly.assembly === 'diy') {
          assemblyTitle = 'DIY kit (you build it)';
          assemblyPrice = 'Free';
          assemblyCents = 0;
        } else {
          const unit = s.assembly.priceCents || 3000;
          assemblyCents = unit * n;
          assemblyTitle = 'Assembled by Brad' + (n > 1 ? ' (×' + n + ')' : '');
          assemblyPrice = this.money(assemblyCents);
        }
      }
      set('[data-cb-sum-assembly-title]', assemblyTitle);
      set('[data-cb-sum-assembly-price]', assemblyPrice);

      const readyEl = this.root.querySelector('[data-cb-sum-ready]');
      if (readyEl) {
        readyEl.textContent = (s.assembly && s.assembly.assembly === 'assembled')
          ? 'Assembled build — ready by ' + this.readyByStr + '.'
          : 'DIY kit — ships in 2-3 days.';
      }

      // Construction of the selected head drives both add-ons below.
      const ironBuild = s.head && (s.head.category === 'iron' || s.head.category === 'putter');

      // Bore-out add-on — one hosel per head, so it scales with the set.
      // Only woods need boring. An antique iron hosel was made for hickory and
      // is already the right diameter, so don't sell a service that does nothing.
      const boreToggle = this.root.querySelector('[data-cb-bore-toggle]');
      const boreRow = this.root.querySelector('[data-cb-sum-bore-row]');
      const boreAddon = this.root.querySelector('[data-cb-addon-bore]');
      const boreApplies = !ironBuild;
      if (boreAddon) boreAddon.hidden = !boreApplies;
      if (!boreApplies && boreToggle) {
        boreToggle.checked = false;
        s.boreOut = false;
      }
      const boreUnit = boreToggle ? parseInt(boreToggle.dataset.cbPriceCents || '0', 10) : 0;
      const boreCents = (boreApplies && s.boreOut) ? boreUnit * n : 0;
      if (boreRow) boreRow.hidden = !(boreApplies && s.boreOut);
      set('[data-cb-sum-bore-price]', '+' + this.money(boreUnit * n));
      set('[data-cb-sum-bore-label]', 'Hosel bore-out' + (n > 1 ? ' (×' + n + ')' : ''));

      // Rebuild-kit add-on — DIY path only, one kit per club. Brad supplies these
      // consumables on assembled builds, so hide the option when Assembled is chosen.
      const isDiy = s.assembly && s.assembly.assembly === 'diy';
      // The kit is whipping thread, wood putty and a back screw — all splice-neck
      // wood parts. An iron build has no use for it, so don't offer it.
      const kitOffered = isDiy && !ironBuild;
      const kitAddon = this.root.querySelector('[data-cb-addon-kit]');
      const kitToggle = this.root.querySelector('[data-cb-kit-toggle]');
      if (kitAddon) kitAddon.hidden = !kitOffered;
      if (!kitOffered && kitToggle) {
        kitToggle.checked = false;
        s.rebuildKit = false;
      }
      const kitUnit = kitToggle ? parseInt(kitToggle.dataset.cbPriceCents || '0', 10) : 0;
      const kitCents = (kitOffered && s.rebuildKit) ? kitUnit * n : 0;
      const kitRow = this.root.querySelector('[data-cb-sum-kit-row]');
      if (kitRow) kitRow.hidden = !(kitOffered && s.rebuildKit);
      set('[data-cb-sum-kit-price]', '+' + this.money(kitUnit * n));
      set('[data-cb-sum-kit-label]', 'Rebuild kit' + (n > 1 ? ' (×' + n + ')' : ''));

      const total = (s.head ? s.head.priceCents : 0)
        + shaftsCents + gripsCents + assemblyCents + boreCents + kitCents;
      set('[data-cb-sum-total]', this.money(total));

      this.summary.hidden = false;
      this.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    bindAddToCart() {
      const addBtn = this.root.querySelector('[data-cb-add]');
      const errEl = this.root.querySelector('[data-cb-error]');
      if (!addBtn) return;

      addBtn.addEventListener('click', async () => {
        const n = this.state.headCount;
        const clubsComplete = Array.isArray(this.state.clubs)
          && this.state.clubs.length === n
          && this.state.clubs.every((c) => c.shaft && c.shaft.variantId && c.grip && c.grip.variantId);
        if (!this.state.head || !this.state.head.variantId || !clubsComplete || !this.state.assembly) {
          if (errEl) {
            errEl.textContent = n > 1
              ? 'Finish picking a shaft and grip for all ' + n + ' clubs, plus a build option, before adding to cart.'
              : 'Pick all four steps before adding to cart.';
            errEl.hidden = false;
          }
          return;
        }

        const buildId = Math.random().toString(36).slice(2, 6).toUpperCase();
        const isAssembled = this.state.assembly.assembly === 'assembled';

        // Hard-fail rather than silently shipping a parts-only build when the
        // fee product is mis-configured. Otherwise the summary shows $X but
        // the cart total is short by the assembly fee.
        if (isAssembled && !this.state.assembly.variantId) {
          if (errEl) {
            errEl.textContent = 'The Assembled-by-Brad option is temporarily unavailable. Pick DIY kit or email brad@oldworldhickorygolf.com.';
            errEl.hidden = false;
          }
          return;
        }

        const leadTimeDays = isAssembled ? 14 : 3;
        const readyByStr = isAssembled ? this.readyByStr : 'ships in 2-3 days';
        const subtitle = isAssembled
          ? 'Assembled build — ready by ' + this.readyByStr
          : 'DIY kit — ships in 2-3 days';

        const buildProps = {
          '_build_id': buildId,
          '_assembly_path': isAssembled ? 'assembled' : 'diy',
          '_lead_time_days': String(leadTimeDays),
          '_ready_by': readyByStr,
          'Build': subtitle
        };

        // One head line for the set, then a shaft + grip line per club.
        const items = [
          {
            id: parseInt(this.state.head.variantId, 10),
            quantity: 1,
            properties: Object.assign({}, buildProps, { '_build_role': 'head', '_head_count': String(n) })
          }
        ];
        this.state.clubs.forEach((club, i) => {
          const clubProp = (n > 1) ? { '_club_index': String(i + 1) } : {};
          items.push({
            id: parseInt(club.shaft.variantId, 10),
            quantity: 1,
            properties: Object.assign({}, buildProps, { '_build_role': 'shaft' }, clubProp)
          });
          items.push({
            id: parseInt(club.grip.variantId, 10),
            quantity: 1,
            properties: Object.assign({}, buildProps, { '_build_role': 'grip' }, clubProp)
          });
        });

        // Assembly fee is per club, so quantity scales with the set.
        if (isAssembled && this.state.assembly.variantId) {
          items.push({
            id: parseInt(this.state.assembly.variantId, 10),
            quantity: n,
            properties: Object.assign({}, buildProps, { '_build_role': 'assembly' })
          });
        }

        // Bore-out applies to both DIY and assembled builds — one per head.
        if (this.state.boreOut) {
          const boreToggle = this.root.querySelector('[data-cb-bore-toggle]');
          const boreVariantId = boreToggle && boreToggle.dataset.cbVariantId;
          if (boreVariantId) {
            items.push({
              id: parseInt(boreVariantId, 10),
              quantity: n,
              properties: Object.assign({}, buildProps, { '_build_role': 'bore-out' })
            });
          }
        }

        // Rebuild kit is a DIY-only add-on (Brad supplies these on assembled builds) — one per club.
        if (!isAssembled && this.state.rebuildKit) {
          const kitToggle = this.root.querySelector('[data-cb-kit-toggle]');
          const kitVariantId = kitToggle && kitToggle.dataset.cbVariantId;
          if (kitVariantId) {
            items.push({
              id: parseInt(kitVariantId, 10),
              quantity: n,
              properties: Object.assign({}, buildProps, { '_build_role': 'kit' })
            });
          }
        }

        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';
        if (errEl) errEl.hidden = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.description || 'Could not add to cart.');
          }
          addBtn.disabled = false;
          addBtn.textContent = 'Add Build to Cart';
          // Open the cart drawer — same experience as every other add-to-cart on the site
          // (free-shipping bar, line items, Checkout). Fall back to the in-page
          // confirmation panel only if the drawer isn't available.
          if (window.owhgOpenCart) {
            window.owhgOpenCart();
          } else {
            if (this.summary) this.summary.hidden = true;
            const added = this.root.querySelector('[data-cb-added]');
            if (added) {
              added.hidden = false;
              added.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        } catch (err) {
          if (errEl) {
            errEl.textContent = err.message || 'Something went wrong adding your build. Try again.';
            errEl.hidden = false;
          }
          addBtn.disabled = false;
          addBtn.textContent = 'Add Build to Cart';
        }
      });
    }
  }

  // ============================================
  // PDP inline build module (single cb-head PDPs)
  // ============================================

  class PdpClubBuilder {
    constructor(root) {
      this.root = root;
      this.headVariant = parseInt(root.dataset.headVariant, 10);
      this.headCents = parseInt(root.dataset.headCents || '0', 10);
      this.headHandle = root.dataset.headHandle || '';
      // Sticky build bar lives just after the module (sibling, not a child).
      this.sticky = document.querySelector('[data-pdp-sticky]');
      this.kitEl = root.querySelector('[data-pdp-kit]');
      this.optionsEl = root.querySelector('[data-pdp-options]'); // shaft + grip pickers
      this.diyEl = root.querySelector('[data-pdp-diy]');         // DIY note + rebuild kit
      this.customizeOpen = false;
      this.state = { shaft: null, grip: null, assembly: { type: 'diy', variant: null, cents: 0 }, upsells: {}, kitOn: false };
      this.initDefaults();
      this.bindPills();
      this.bindUpsells();
      this.bindKit();
      this.bindCustomize();
      this.bindAdd();
      this.bindSticky();
      this.syncBuildPath();
      this.renderTotal();
      // Only the B bucket actually sees the module; tag the bucket either way.
      this.track('owhg_pdp_build_bucket', {});
      if (document.documentElement.classList.contains('pdp-build-on')) {
        this.track('owhg_pdp_build_impression', {});
      }
    }

    pillData(el) {
      return {
        variant: el.dataset.variant ? parseInt(el.dataset.variant, 10) : null,
        cents: parseInt(el.dataset.cents || '0', 10),
        label: el.dataset.label || ''
      };
    }

    initDefaults() {
      const ss = this.root.querySelector('[data-pdp-pick="shaft"].is-selected') || this.root.querySelector('[data-pdp-pick="shaft"]:not([disabled])');
      if (ss) { ss.classList.add('is-selected'); this.state.shaft = this.pillData(ss); }
      const gs = this.root.querySelector('[data-pdp-pick="grip"].is-selected') || this.root.querySelector('[data-pdp-pick="grip"]:not([disabled])');
      if (gs) { gs.classList.add('is-selected'); this.state.grip = this.pillData(gs); }
      const as = this.root.querySelector('[data-pdp-pick="assembly"].is-selected');
      if (as) this.state.assembly = { type: as.dataset.assembly, variant: as.dataset.variant ? parseInt(as.dataset.variant, 10) : null, cents: parseInt(as.dataset.cents || '0', 10) };
    }

    bindPills() {
      this.root.querySelectorAll('[data-pdp-pick]').forEach((p) => {
        p.addEventListener('click', () => {
          if (p.hasAttribute('disabled')) return;
          const role = p.dataset.pdpPick;
          this.root.querySelectorAll(`[data-pdp-pick="${role}"]`).forEach((s) => s.classList.remove('is-selected'));
          p.classList.add('is-selected');
          if (role === 'assembly') {
            this.state.assembly = { type: p.dataset.assembly, variant: p.dataset.variant ? parseInt(p.dataset.variant, 10) : null, cents: parseInt(p.dataset.cents || '0', 10) };
            this.customizeOpen = false; // reset DIY customize when switching path
            this.syncBuildPath();
          } else {
            this.state[role] = this.pillData(p);
          }
          this.renderTotal();
        });
      });
    }

    bindUpsells() {
      this.root.querySelectorAll('[data-pdp-upsell]').forEach((card) => {
        const cb = card.querySelector('input[type="checkbox"]');
        card.addEventListener('change', () => {
          const id = parseInt(card.dataset.variant, 10);
          if (cb && cb.checked) {
            this.state.upsells[id] = { variant: id, cents: parseInt(card.dataset.cents || '0', 10), label: card.dataset.label || '' };
            card.classList.add('is-selected');
          } else {
            delete this.state.upsells[id];
            card.classList.remove('is-selected');
          }
          this.renderTotal();
        });
      });
    }

    bindKit() {
      if (!this.kitEl) return;
      const cb = this.kitEl.querySelector('[data-pdp-kit-check]');
      this.kitEl.addEventListener('change', () => {
        this.state.kitOn = !!(cb && cb.checked);
        this.kitEl.classList.toggle('is-selected', this.state.kitOn);
        this.renderTotal();
      });
    }

    bindCustomize() {
      const btn = this.root.querySelector('[data-pdp-customize]');
      if (btn) btn.addEventListener('click', () => {
        this.customizeOpen = true;
        this.syncBuildPath();
        if (this.optionsEl) this.optionsEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    // DIY hides the shaft/grip pickers (sensible defaults still ship in the box, so
    // nothing is forced) and shows the DIY note + rebuild kit. Assembled shows the
    // pickers (Brad builds to spec) and hides the DIY block. DIY can opt in via the
    // customize link.
    syncBuildPath() {
      const isDiy = this.state.assembly.type === 'diy';
      const showOptions = !isDiy || this.customizeOpen;
      if (this.optionsEl) this.optionsEl.hidden = !showOptions;
      if (this.diyEl) this.diyEl.hidden = !isDiy;
      this.syncKit();
      this.renderTotal();
    }

    // The rebuild kit is a DIY-only option. Hide + clear it on assembled builds
    // (Brad supplies those consumables), mirroring the full builder.
    syncKit() {
      if (!this.kitEl) return;
      const isDiy = this.state.assembly.type === 'diy';
      this.kitEl.hidden = !isDiy;
      if (!isDiy) {
        const cb = this.kitEl.querySelector('[data-pdp-kit-check]');
        if (cb) cb.checked = false;
        this.kitEl.classList.remove('is-selected');
        this.state.kitOn = false;
      }
    }

    kitCents() {
      return (this.kitEl && this.state.kitOn && this.state.assembly.type === 'diy')
        ? parseInt(this.kitEl.dataset.cents || '0', 10) : 0;
    }

    money(c) { return '$' + (c / 100).toFixed(2); }

    // Shaft + grip are only part of the build when Brad assembles, or when a DIY
    // buyer opts in via "Choose them yourself". DIY on its own is just the head,
    // so the total doesn't silently include parts the customer can't see.
    partsIncluded() {
      return this.state.assembly.type === 'assembled' || this.customizeOpen;
    }

    total() {
      let t = this.headCents + this.state.assembly.cents;
      if (this.partsIncluded()) {
        t += (this.state.shaft ? this.state.shaft.cents : 0) + (this.state.grip ? this.state.grip.cents : 0);
      }
      Object.keys(this.state.upsells).forEach((k) => { t += this.state.upsells[k].cents; });
      t += this.kitCents();
      return t;
    }

    renderTotal() {
      const formatted = this.money(this.total());
      const el = this.root.querySelector('[data-pdp-total]');
      if (el) el.textContent = formatted;
      if (this.sticky) {
        const st = this.sticky.querySelector('[data-pdp-sticky-total]');
        if (st) st.textContent = formatted;
      }
    }

    track(event, data) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: event, bucket: window.__owhgBuildBucket || 'B', head: this.headHandle }, data));
      } catch (e) { /* analytics is best-effort */ }
    }

    bindAdd() {
      const btn = this.root.querySelector('[data-pdp-add]');
      if (btn) btn.addEventListener('click', () => this.doAdd(btn));
      const buyBtn = this.root.querySelector('[data-pdp-buy]');
      if (buyBtn) buyBtn.addEventListener('click', () => this.doAdd(buyBtn, true));
    }

    // Sticky bar: same add path, plus reveal it once the main CTA scrolls off
    // (mobile). The standard sticky bar is hidden in bucket B via CSS.
    bindSticky() {
      if (!this.sticky) return;
      const sbtn = this.sticky.querySelector('[data-pdp-add-sticky]');
      if (sbtn) sbtn.addEventListener('click', () => this.doAdd(sbtn));
      const buyBtn = this.sticky.querySelector('[data-pdp-buy-sticky]');
      if (buyBtn) buyBtn.addEventListener('click', () => this.doAdd(buyBtn, true));

      const mainBtn = this.root.querySelector('[data-pdp-add]');
      if (mainBtn && 'IntersectionObserver' in window) {
        const obs = new IntersectionObserver(([entry]) => {
          this.sticky.classList.toggle('is-visible', !entry.isIntersecting);
        }, { threshold: 0 });
        obs.observe(mainBtn);
      }
    }

    async doAdd(btn, checkout) {
      const err = this.root.querySelector('[data-pdp-error]');
      const isAssembled = this.state.assembly.type === 'assembled';
      const includeParts = this.partsIncluded();
      // Assembled needs a shaft + grip so Brad can build it. DIY (no customize) is
      // just the head, so no parts are required.
      if (includeParts && (!this.state.shaft || !this.state.shaft.variant || !this.state.grip || !this.state.grip.variant)) {
        if (err) { err.textContent = 'Pick a shaft and a grip to build.'; err.hidden = false; }
        return;
      }
      const buildId = Math.random().toString(36).slice(2, 6).toUpperCase();
      const props = { '_build_id': buildId, '_assembly_path': isAssembled ? 'assembled' : 'diy', '_build_source': 'pdp', 'Build': isAssembled ? 'Assembled build' : 'DIY kit' };
      const items = [
        { id: this.headVariant, quantity: 1, properties: Object.assign({}, props, { '_build_role': 'head', '_head_count': '1' }) }
      ];
      if (includeParts && this.state.shaft && this.state.shaft.variant) {
        items.push({ id: this.state.shaft.variant, quantity: 1, properties: Object.assign({}, props, { '_build_role': 'shaft' }) });
      }
      if (includeParts && this.state.grip && this.state.grip.variant) {
        items.push({ id: this.state.grip.variant, quantity: 1, properties: Object.assign({}, props, { '_build_role': 'grip' }) });
      }
      if (isAssembled && this.state.assembly.variant) {
        items.push({ id: this.state.assembly.variant, quantity: 1, properties: Object.assign({}, props, { '_build_role': 'assembly' }) });
      }
      Object.keys(this.state.upsells).forEach((k) => {
        const u = this.state.upsells[k];
        items.push({ id: u.variant, quantity: 1, properties: Object.assign({}, props, { '_build_role': 'upsell' }) });
      });
      // DIY-only rebuild kit
      if (!isAssembled && this.state.kitOn && this.kitEl && this.kitEl.dataset.variant) {
        items.push({ id: parseInt(this.kitEl.dataset.variant, 10), quantity: 1, properties: Object.assign({}, props, { '_build_role': 'kit' }) });
      }
      btn.disabled = true;
      const orig = btn.textContent;
      btn.textContent = 'Adding…';
      if (err) err.hidden = true;
      this.track('owhg_pdp_build_atc', { value: (this.total() / 100), upsells: Object.keys(this.state.upsells).length, assembled: isAssembled });
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.description || 'Could not add to cart.'); }
        if (checkout) {
          window.location.href = '/checkout';
        } else if (window.owhgOpenCart) {
          window.owhgOpenCart();
          btn.disabled = false;
          btn.textContent = orig;
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        if (err) { err.textContent = e.message || 'Something went wrong. Try again.'; err.hidden = false; }
        btn.disabled = false;
        btn.textContent = orig;
      }
    }
  }

  // ============================================
  // Accessory quick-add (standalone "Finish your setup" cards)
  // ============================================

  class AccessoryQuickAdd {
    constructor() {
      document.querySelectorAll('[data-acc-add]').forEach((btn) => {
        btn.addEventListener('click', () => this.add(btn));
      });
    }

    async add(btn) {
      const id = parseInt(btn.dataset.variant, 10);
      if (!id) return;
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1, properties: { '_source': 'accessory-suggestion' } }] })
        });
        if (!res.ok) throw new Error('add failed');
        btn.textContent = 'Added ✓';
        btn.classList.add('is-added');
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'owhg_accessory_add', variant: id, title: btn.dataset.title || '' });
        } catch (e) { /* analytics best-effort */ }
        if (window.owhgOpenCart) window.owhgOpenCart();
        setTimeout(() => { btn.disabled = false; btn.textContent = orig; btn.classList.remove('is-added'); }, 1800);
      } catch (e) {
        btn.disabled = false;
        btn.textContent = orig;
      }
    }
  }

  // ============================================
  // Accessory picker (visual thumbnail strip, inventory-driven)
  // ============================================
  // Each "Finish your setup" slot shows a strip of in-stock thumbnails. We verify
  // each against live stock (cache-proof for one-of-a-kind items), drop any that
  // are sold, and update the card (image / name / price / add wiring) when the
  // shopper taps a thumbnail. Hides the slot if a whole category sells out.

  class AccessorySelect {
    constructor(slot) {
      this.slot = slot;
      this.mode = slot.dataset.accMode || 'standalone';
      this.thumbs = slot.querySelector('[data-acc-thumbs]');
      this.card = slot.querySelector('[data-acc-card]');
      this.cache = {};
      this.ready = this.init();
    }

    async available(handle) {
      if (handle in this.cache) return this.cache[handle];
      try {
        const res = await fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) { this.cache[handle] = true; return true; } // fail-open
        const data = await res.json();
        this.cache[handle] = !!data.available;
        return this.cache[handle];
      } catch (e) {
        this.cache[handle] = true; // network hiccup: don't blank the slot
        return true;
      }
    }

    money(cents) {
      const n = (parseInt(cents, 10) || 0) / 100;
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    options() {
      return this.thumbs ? Array.from(this.thumbs.querySelectorAll('[data-acc-option]')) : [];
    }

    async init() {
      if (!this.thumbs) {
        // single option, no strip — just verify it's still in stock
        const ok = await this.available(this.slot.dataset.accHandle);
        if (!ok) this.slot.hidden = true;
        return;
      }

      // prune any option that's actually sold right now
      const opts = this.options();
      const live = await Promise.all(opts.map((o) => this.available(o.dataset.handle)));
      opts.forEach((o, i) => { if (!live[i]) o.remove(); });

      const remaining = this.options();
      if (remaining.length === 0) { this.slot.hidden = true; return; }

      remaining.forEach((o) => {
        o.tabIndex = -1;
        o.addEventListener('click', () => this.select(o));
      });
      this.thumbs.addEventListener('keydown', (e) => this.onKey(e));

      let sel = remaining.find((o) => o.getAttribute('aria-selected') === 'true');
      if (!sel) sel = remaining[0];
      this.select(sel);

      // nothing to choose between → hide the strip, keep the card
      if (remaining.length === 1) this.thumbs.hidden = true;
    }

    onKey(e) {
      const opts = this.options();
      if (!opts.length) return;
      const cur = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      let ni = cur;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { ni = Math.min(opts.length - 1, cur + 1); e.preventDefault(); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { ni = Math.max(0, cur - 1); e.preventDefault(); }
      else return;
      if (ni !== cur) { this.select(opts[ni]); opts[ni].focus(); }
    }

    select(o) {
      if (!o) return;
      this.options().forEach((x) => {
        x.setAttribute('aria-selected', 'false');
        x.classList.remove('is-active');
        x.tabIndex = -1;
      });
      o.setAttribute('aria-selected', 'true');
      o.classList.add('is-active');
      o.tabIndex = 0;
      this.apply(o);
    }

    apply(o) {
      const img = this.card.querySelector('[data-acc-img]');
      if (img) {
        if (o.dataset.img) img.src = o.dataset.img;
        img.alt = o.dataset.title || '';
      }
      const link = this.card.querySelector('[data-acc-img-link]');
      if (link && o.dataset.url) link.href = o.dataset.url;
      const name = this.card.querySelector('[data-acc-name]');
      if (name) {
        name.textContent = o.dataset.title || '';
        if (name.tagName === 'A' && o.dataset.url) name.href = o.dataset.url;
      }
      const price = this.card.querySelector('[data-acc-price]');
      if (price) price.textContent = (this.mode === 'build' ? '+' : '') + this.money(o.dataset.cents);

      if (this.mode === 'build') {
        // Re-point the upsell. If it's checked, uncheck→recheck so the build
        // total swaps the old variant for the new one via the existing handler.
        const labelCard = this.card.querySelector('[data-pdp-upsell]');
        if (!labelCard) return;
        const cb = labelCard.querySelector('input[type="checkbox"]');
        const wasChecked = cb && cb.checked;
        if (wasChecked) { cb.checked = false; labelCard.dispatchEvent(new Event('change')); }
        labelCard.dataset.variant = o.dataset.variant;
        labelCard.dataset.cents = o.dataset.cents;
        labelCard.dataset.label = o.dataset.title || '';
        if (wasChecked) { cb.checked = true; labelCard.dispatchEvent(new Event('change')); }
      } else {
        const btn = this.card.querySelector('[data-acc-add]');
        if (btn) {
          btn.dataset.variant = o.dataset.variant;
          btn.dataset.title = o.dataset.title || '';
          btn.disabled = false;
          btn.textContent = 'Add';
          btn.classList.remove('is-added');
        }
      }
    }
  }

  // ============================================
  // Cart Drawer (slide-out, Allbirds-style)
  // ============================================
  // Opens on Add to Cart instead of redirecting to /cart. Renders the live cart from
  // /cart.js, supports qty change + remove, and shows a free-shipping progress bar.

  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.body = root.querySelector('[data-cart-body]');
      this.foot = root.querySelector('[data-cart-foot]');
      this.emptyEl = root.querySelector('[data-cart-empty]');
      this.countEl = root.querySelector('[data-cart-drawer-count]');
      this.subtotalEl = root.querySelector('[data-cart-subtotal]');
      this.shipEl = root.querySelector('[data-cart-ship]');
      this.shipMsg = root.querySelector('[data-cart-ship-msg]');
      this.shipFill = root.querySelector('[data-cart-ship-fill]');
      this.freeShip = parseInt(root.dataset.freeShipCents || '0', 10);
      this.bind();
    }

    bind() {
      this.root.querySelectorAll('[data-cart-close]').forEach((el) =>
        el.addEventListener('click', (e) => { if (!el.getAttribute('href')) e.preventDefault(); this.close(); })
      );
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen()) this.close(); });
      // qty +/- and remove (event delegation, survives re-render)
      this.body.addEventListener('click', (e) => {
        const line = e.target.closest('[data-cart-line]');
        if (!line) return;
        const key = line.dataset.cartLine;
        const qty = parseInt(line.dataset.qty, 10) || 1;
        if (e.target.closest('[data-cart-remove]')) this.change(key, 0);
        else if (e.target.closest('[data-cart-plus]')) this.change(key, qty + 1);
        else if (e.target.closest('[data-cart-minus]')) this.change(key, Math.max(0, qty - 1));
      });
    }

    isOpen() { return this.root.getAttribute('aria-hidden') === 'false'; }

    async open() {
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      await this.refresh();
    }

    close() {
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
    }

    money(c) { return '$' + ((parseInt(c, 10) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

    async refresh() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        this.render(await res.json());
      } catch (e) { /* keep prior state */ }
    }

    render(cart) {
      const count = cart.item_count || 0;
      if (this.countEl) this.countEl.textContent = count ? '(' + count + ')' : '';
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count || ''; el.hidden = count === 0; });

      if (count === 0) {
        this.body.innerHTML = '';
        this.foot.hidden = true;
        this.shipEl.hidden = true;
        this.emptyEl.hidden = false;
        return;
      }
      this.emptyEl.hidden = true;
      this.foot.hidden = false;
      this.body.innerHTML = cart.items.map((it) => this.lineHTML(it)).join('');
      this.subtotalEl.textContent = this.money(cart.items_subtotal_price);
      this.renderShip(cart.items_subtotal_price);
    }

    lineHTML(it) {
      let img = it.image || '';
      if (img) img = img.replace(/(\.(?:jpg|jpeg|png|gif|webp))(\?|$)/i, '_120x$1$2');
      const variant = (it.product_has_only_default_variant ? '' : (it.variant_title || ''));
      return '' +
        '<div class="cart-drawer__item" data-cart-line="' + it.key + '" data-qty="' + it.quantity + '">' +
          '<a href="' + it.url + '" class="cart-drawer__item-media">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div class="cart-drawer__item-info">' +
            '<a href="' + it.url + '" class="cart-drawer__item-title">' + it.product_title + '</a>' +
            (variant ? '<p class="cart-drawer__item-variant">' + variant + '</p>' : '') +
            '<div class="cart-drawer__qty">' +
              '<button type="button" data-cart-minus aria-label="Decrease quantity">&minus;</button>' +
              '<span class="cart-drawer__qty-val">' + it.quantity + '</span>' +
              '<button type="button" data-cart-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-drawer__remove" data-cart-remove>Remove</button>' +
          '</div>' +
          '<div class="cart-drawer__item-price">' + this.money(it.final_line_price) + '</div>' +
        '</div>';
    }

    renderShip(subtotal) {
      if (!this.freeShip) { this.shipEl.hidden = true; return; }
      this.shipEl.hidden = false;
      const remaining = this.freeShip - subtotal;
      const pct = Math.min(100, Math.round((subtotal / this.freeShip) * 100));
      this.shipMsg.innerHTML = remaining > 0
        ? "You're <strong>" + this.money(remaining) + "</strong> away from free shipping."
        : "You've earned <strong>free shipping</strong>.";
      this.shipFill.style.width = pct + '%';
    }

    async change(key, qty) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        });
        this.render(await res.json());
      } catch (e) { /* noop */ }
    }
  }

  // ============================================
  // Initialize Everything
  // ============================================

  document.addEventListener('DOMContentLoaded', () => {
    // Global components
    new MobileNav();
    new SearchOverlay();
    new QuickAdd();
    new SmoothScroll();

    // Page-specific components
    const productGallery = document.querySelector('[data-product-gallery]');
    if (productGallery) new ProductGallery(productGallery);

    const productForm = document.querySelector('[data-product-form]');
    if (productForm) new ProductForm(productForm);

    const productTabs = document.querySelector('[data-product-tabs]');
    if (productTabs) new ProductTabs(productTabs);

    const heroCarousel = document.querySelector('[data-hero-carousel]');
    if (heroCarousel) new HeroCarousel(heroCarousel);

    const testimonialsSlider = document.querySelector('[data-testimonials-slider]');
    if (testimonialsSlider) new TestimonialsSlider(testimonialsSlider);

    const cart = document.querySelector('[data-cart-form]');
    if (cart) new Cart();

    const collectionFilters = document.querySelector('[data-filter-form]');
    if (collectionFilters) new CollectionFilters();

    new ViewToggle();

    const stickyBar = document.querySelector('[data-sticky-bar]');
    if (stickyBar) new StickyBar();

    const clubBuilder = document.querySelector('[data-club-builder]');
    if (clubBuilder) new ClubBuilder(clubBuilder);

    const pdpBuild = document.querySelector('[data-pdp-build]');
    if (pdpBuild) new PdpClubBuilder(pdpBuild);

    if (document.querySelector('[data-acc-add]')) new AccessoryQuickAdd();

    document.querySelectorAll('[data-acc-slot]').forEach((slot) => new AccessorySelect(slot));

    // Cart drawer — global, opens on add-to-cart and via the header cart icon
    const cartDrawerEl = document.querySelector('[data-cart-drawer]');
    if (cartDrawerEl) {
      window.OWHGCart = new CartDrawer(cartDrawerEl);
      window.owhgOpenCart = () => { if (window.OWHGCart) window.OWHGCart.open(); };
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) =>
        el.addEventListener('click', (e) => { e.preventDefault(); window.owhgOpenCart(); })
      );
    }
  });

})();
